import '@qadra/frontend-common/assets/fonts/gteesti.css';
import '@qadra/frontend-common/assets/fonts/literata.css';
import '@qadra/frontend-common/assets/styles/spinner.css';

import React from 'react';
import { TranslateProvider } from '@federico.moretti/react-translate';
import { Provider } from '@qadra/frontend-common/redux';
import { queryString } from '@qadra/frontend-common';
import { ThemeProvider } from '@qadra/frontend-common-ts';
import { Route, Switch, Router, Redirect } from '@qadra/frontend-common/router';
import { SpinnerLogo } from '@qadra/frontend-common/atoms';
import { monitoring } from '@qadra/frontend-common/tracking';
import { QueryClient, QueryClientProvider } from 'react-query';
import { store, history } from './redux/store';
import Login from './components/Login';
import Layout from './components/Layout';
import PasswordForgot from './components/PasswordForgot';
import PasswordReset from './components/PasswordReset';
import Activate from './components/Activate';
import ConfirmEmail from './components/ConfirmEmail';
import { useGetTranslations } from './utils/translations';
import { useGetSettings } from './utils/settings';
import { paths } from './utils';

const isDev = process.env.NODE_ENV === 'development';

const queryClient = new QueryClient({
  defaultOptions: { queries: { refetchOnWindowFocus: false } },
});

function getTranslationsPath(obj) {
  if (obj?.staticFilesHost && obj?.translationsPath)
    return `${obj.staticFilesHost}${obj.translationsPath}`;
}

function App(props) {
  const { settings } = props;

  const translationsPath = getTranslationsPath(settings?.data);
  const translations = useGetTranslations(translationsPath);

  const { search, host } = window.location;
  const { showTranslationIds } = queryString.parse(search);
  const canShowIds = host.includes('staging') || isDev;

  if (translations.isLoading || !translations.isSuccess) return <SpinnerLogo />;

  const defaultLanguage = translations.data.meta.languageCodes.includes(navigator.language)
    ? navigator.language
    : translations.data.meta.defaultLanguage;

  return (
    <TranslateProvider
      translations={translations.data}
      defaultLanguage={defaultLanguage}
      fallbackLanguage={translations.data.meta.defaultLanguage}
      showIds={canShowIds && showTranslationIds}
    >
      <Provider store={store}>
        <Router history={history}>
          <Layout>
            <Switch>
              <Route path={paths.login} exact>
                <Login />
              </Route>
              <Route path={paths.activate} exact>
                <Activate />
              </Route>
              <Route path={paths.confirmEmail} exact>
                <ConfirmEmail />
              </Route>
              <Route path={paths.forgotPassword} exact>
                <PasswordForgot />
              </Route>
              <Route path={paths.resetPassword} exact>
                <PasswordReset />
              </Route>
              <Redirect to={paths.login} />
            </Switch>
          </Layout>
        </Router>
      </Provider>
    </TranslateProvider>
  );
}

function SettingsApp() {
  const settings = useGetSettings();

  const sentryDsn = settings?.data?.sentryDsn;
  const environment = settings?.data?.environment ?? 'development';
  React.useEffect(() => {
    monitoring.init({ dsn: sentryDsn, environment });
  }, [sentryDsn, environment]);

  if (settings.isLoading || !settings.isSuccess) return <SpinnerLogo />;

  return <App settings={settings} />;
}

function ProvidersApp() {
  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <SettingsApp />
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default ProvidersApp;
